import { Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import {
  getOfferListFromStorage,
  WebshopUtils,
} from 'src/app/shared/webshop/webshop-utils';

import {
  AnalyticsService,
  LanguageService,
  TranslationService,
} from '@proximus/ng-common';
import {
  OptionDetailsBackgroundService,
  OptionParams,
} from '../../option-details-background.service';
import { OrderButtonComponent } from 'src/app/shared/order-button/order-button.component';
import { OptionLockService } from 'src/app/services/option-lock.service';
import { OptionConfigurationLockService } from 'src/app/services/option-configuration-lock.service';
import {
  AlertsService,
  ModalService,
  PxModalMessageService,
} from '@proximus/ng-common-components';
import { ApplicationState } from 'src/app/store/reducers';
import { Store } from '@ngrx/store';
import { getMprdProductType } from '@dch/products-business-logic';
import { Subscription as RxJSSubscription } from 'rxjs';
import { isStpOption, onResult, isIffOption } from 'src/app/shared/order-utils/order-utils';
import { OrderButtonType } from '../../disney-plus-order-button/disney-plus-order-button.component';
import { BrandService } from 'src/app/services/brand.service';
import {
  SCARLET_SPORTS_PREMIUM,
  SCARLET_SPORTS_STANDARD,
  SPORTS_PREMIUM,
  SPORTS_STANDARD,
} from 'src/app/details/constants';

const PRODUCT_TYPE = 'RPO';

@Component({
  selector: 'mprd-sports-order-button',
  templateUrl: './sports-order-button.component.html',
})
export class SportsOrderButtonComponent
  extends OrderButtonComponent
  implements OnInit, OnDestroy
{
  public ButtonType = OrderButtonType;

  @Input() buttonType: OrderButtonType;
  @Input() isScarletOption: string;

  private readonly registerOptionParamsExecutedSubscription: RxJSSubscription;
  private readonly buttonStateChangeExecutedSubscription: RxJSSubscription;

  public service: OptionDetailsBackgroundService;

  private queryParamToken: string;
  public isLoadingToken: boolean;

  constructor(
    protected modalService: ModalService,
    store: Store<ApplicationState>,
    ngZone: NgZone,
    analyticsService: AnalyticsService,
    optionLockService: OptionLockService,
    optionConfigurationLockService: OptionConfigurationLockService,
    translationService: TranslationService,
    protected pxModalMessageService: PxModalMessageService,
    protected languageService: LanguageService,
    private readonly optionDetailsBackgroundService: OptionDetailsBackgroundService,
    private readonly alertsService: AlertsService,
    private readonly brandService: BrandService,
  ) {
    super(
      modalService,
      store,
      ngZone,
      analyticsService,
      optionLockService,
      optionConfigurationLockService,
      translationService,
    );

    this.service = optionDetailsBackgroundService;

    // Guarantees that the option params are set here from OptionDetailsBackgroundService whithout using inputs
    this.registerOptionParamsExecutedSubscription =
      this.service.registerOptionParamsExecutedSubject.subscribe(() => {
        this.initializeOrderButton(this.service.getOptionParams());
      });

    this.buttonStateChangeExecutedSubscription =
      this.buttonStateChangeExecutedSubject.subscribe(() => {
        this.service.setLoadingState(this.isLoadingState);

        if (
          isStpOption(this.option, this.isSwap) ||
          this.isSwap ||
          isIffOption(this.option)
        ) {
          // Disable all buttons if any of the following conditions below are true
          this.service.setAllButtonsDisabled(
            this.orderDisabled || this.isPendingInCart || this.pendingOrder,
          );
        }
      });
  }

  ngOnInit(): void {
    this.initializeOrderButton(this.service.getOptionParams());
  }

  ngOnDestroy(): void {
    this.registerOptionParamsExecutedSubscription.unsubscribe();
    this.buttonStateChangeExecutedSubscription.unsubscribe();
  }

  public getButtonParams() {
    const genericClasses = [];

    if (this.service.isLoadingState) {
      genericClasses.push('rs-btn-waiting');
    }

    if (this.service.isDisabledState) {
      genericClasses.push('rs-btn-disabled');
    }

    return {
      [OrderButtonType.ORDER_STANDARD]: {
        labelKey: 'option.details.sports.standard.order',
        class: ['rs-btn', 'rs-btn-order', 'rs-maxw'].concat(genericClasses),
        disabled: this.service.isLoadingState,
        click: () => this.executeSportsOrder(),
      },
      [OrderButtonType.ORDER_PREMIUM]: {
        labelKey: 'option.details.sports.premium.order',
        class: ['rs-btn', 'rs-btn-order', 'rs-maxw'].concat(genericClasses),
        disabled: this.service.isLoadingState,
        click: () => this.executeSportsOrder(),
      },

      [OrderButtonType.UPGRADE]: {
        labelKey: 'option.details.sports.upgrade',
        class: ['rs-btn', 'rs-btn-order', 'rs-maxw'].concat(genericClasses),
        disabled: this.service.isLoadingState,
        click: () => this.executeSportsOrder(),
      },
      [OrderButtonType.DOWNGRADE]: {
        labelKey: 'option.details.sports.downgrade',
        class: ['rs-btn', 'rs-btn-third', 'rs-btn-neg', 'rs-maxw'].concat(
          genericClasses,
        ),
        disabled: this.service.isLoadingState,
        showRightIcon: !this.service.isLoadingState,
        click: () => this.executeSportsOrder(),
      },
      [OrderButtonType.CEASE]: {
        labelKey: 'option.details.sports.cease',
        class: ['rs-btn', 'rs-btn-second', 'rs-btn-neg'].concat(genericClasses),
        disabled: this.service.isLoadingState,
        click: () => this.executeSportsOrder(),
      },
    }[this.buttonType];
  }

  private initializeOrderButton(optionParams: OptionParams) {
    this.setOptionParams(optionParams);
    this.internalOnChanges();
    this.setupResultEmitSubscription();
  }

  private setOptionParams(optionParams: OptionParams) {
    const {
      option,
      selectedProduct,
      isDeepLink,
      deepLinkStatus,
      treatmentCode,
      queryParamToken,
    } = optionParams;

    this.option = option;
    this.selectedProduct = selectedProduct;
    this.isDeepLink = isDeepLink;
    this.deepLinkStatus = deepLinkStatus;
    this.treatmentCode = treatmentCode;
    this.queryParamToken = queryParamToken;
  }

  private setupResultEmitSubscription() {
    // Used to show the confirmation or warning messages
    this.resultEmit.subscribe((event) => {
      const isTvProduct = getMprdProductType(this.selectedProduct) === 'tv';

      onResult(
        event,
        undefined,
        this.selectedProduct,
        this.option,
        this.translationService,
        this.languageService,
        this.modalService,
        this.pxModalMessageService,
        this.alertsService,
        this.treatmentCode,
        isTvProduct,
        false,
      );
    });
  }

  public executeSportsOrder() {
    this.loading = true;
    this.resultEmit.emit({ orderingInProgress: true });

    this.calculateLoadingState();

    const isTryAndBuyOptInOrOptOut =
      this.option?.trialOption?.endOfTrialDecision !== undefined;

    if (isTryAndBuyOptInOrOptOut) {
      this.executeTrialFlow();
    } else {
      this.executeTaggingEventFlow();

      const payload = {
        action: this.getOrderAction(),
        isReverse: false,
        isPremium: false,
        product: {
          mpcUuid: this.getDPMpcUuid(),
          type: PRODUCT_TYPE,
          chargeEvent: this.getChargeEvent(),
          chargeCode: this.getChargeCode(),
          promoCode: undefined,
        },
      };

      WebshopUtils.addProduct(
        payload.isReverse,
        payload.product.mpcUuid,
        payload.product.type,
        payload.product.chargeEvent,
        payload.product.chargeCode,
        payload.action,
        getOfferListFromStorage(this.treatmentCode),
        payload.product.promoCode,
        payload.isPremium,
      );
    }
  }

  private getOrderAction(): 'add' | 'remove' {
    if (OrderButtonType.CEASE === this.buttonType) {
      return 'remove';
    } else {
      return 'add';
    }
  }

  

  private getDPMpcUuid() {
    const standardButtonTypes = [
      OrderButtonType.ORDER_STANDARD,
      OrderButtonType.DOWNGRADE,
    ];

    const premiumButtonTypes = [
      OrderButtonType.UPGRADE,
      OrderButtonType.ORDER_PREMIUM,
    ];

    if (standardButtonTypes.includes(this.buttonType)) {
      return this.isScarletOption ? SCARLET_SPORTS_STANDARD : SPORTS_STANDARD;
    }

    if (premiumButtonTypes.includes(this.buttonType)) {
      return this.isScarletOption ? SCARLET_SPORTS_PREMIUM : SPORTS_PREMIUM;
    }
    return this.option.optionCodeForDescription || this.option.mpcUuid;
  }
}
